import { Modal, Stack, NumberInput, Text, Textarea, Group, Button } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { formatSom } from '../../lib/format';
import { apiError } from '../../lib/journal';

interface Props {
  opened: boolean;
  onClose: () => void;
  userId: string;
  /** Qaysi oy uchun ("YYYY-MM") — `Expense.forMonth`. */
  month: string;
  /** Shu oy uchun qolgan (yozilgan − berilgan); manfiy — ortiqcha berilgan. */
  qolgan: number;
  /** Sarlavhada kimga berilayotgani (Maosh sahifasidagi qator). */
  xodim?: string;
}

/**
 * MAOSH BERISH oynasi — `POST /expenses` (SALARY, `forMonth`); avans ham shu yo'l bilan.
 * Maosh sahifasi (`pages/Payroll.tsx`) va xodim kartasining Maosh yorlig'i uchun YAGONA oyna.
 */
export function MaoshBerishModal({ opened, onClose, userId, month, qolgan, xodim }: Props) {
  const { t } = useTranslation();
  return (
    // Sarlavha — uni ochadigan tugma bilan AYNI kalit ("Maosh berish")
    <Modal opened={opened} onClose={onClose} title={xodim ? `${t('payrollPage.pay')} · ${xodim}` : t('payrollPage.pay')}>
      {/* Forma oyna har ochilganda (va boshqa xodim/oyga o'tilganda — `key`) qaytadan yaratiladi:
          Modal yopiq turganda ichini chizmaydi. Summa sukuti — ochilgan paytdagi qolgan,
          60 s lik yangilanish kiritilayotgan summani almashtirmaydi. */}
      <Forma key={`${userId}:${month}`} onClose={onClose} userId={userId} month={month} qolgan={qolgan} />
    </Modal>
  );
}

function Forma({ onClose, userId, month, qolgan }: Omit<Props, 'opened' | 'xodim'>) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const form = useForm({
    initialValues: { amount: Math.max(0, qolgan), note: '' },
    validate: { amount: (v) => (Number(v) > 0 ? null : t('expenses.amountRequired')) },
  });
  const payMut = useMutation({
    mutationFn: (v: { amount: number; note: string }) =>
      api.post('/expenses', { category: 'SALARY', amount: v.amount, userId, forMonth: month, note: v.note || undefined }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('staffDetail.paid') });
      qc.invalidateQueries({ queryKey: ['salary-compute', userId] });
      qc.invalidateQueries({ queryKey: ['salary-history', userId] });
      // Maosh — SALARY xarajati: Maosh sahifasi, Xodimlar ro'yxatidagi "qolgan" badge
      // (payroll-summary) va xarajat ko'rinadigan hamma joy — `ExpenseFormModal` bilan AYNI ro'yxat
      // (Xarajatlar sahifasi, kategoriya/tur jami va detali, moliya kartalari, kassa hisoboti).
      qc.invalidateQueries({ queryKey: ['payroll-summary'] });
      qc.invalidateQueries({ queryKey: ['payroll-oylik'] });
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense-detail'] });
      qc.invalidateQueries({ queryKey: ['expenses-summary'] });
      qc.invalidateQueries({ queryKey: ['finance'] });
      qc.invalidateQueries({ queryKey: ['cashbook'] });
      onClose();
    },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  return (
    <form onSubmit={form.onSubmit((v) => payMut.mutate(v))}>
      <Stack>
        <NumberInput label={t('staffDetail.amount')} min={0} step={100000} thousandSeparator=" " suffix={` ${t('currency.som')}`} required data-autofocus {...form.getInputProps('amount')} />
        {form.values.amount > Math.max(0, qolgan) && (
          <Text size="xs" c="orange.7">{t('staffDetail.overpay')} ({formatSom(Math.max(0, qolgan))})</Text>
        )}
        <Textarea label={t('staffDetail.note')} autosize minRows={1} {...form.getInputProps('note')} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" loading={payMut.isPending}>{t('payrollPage.payConfirm')}</Button>
        </Group>
      </Stack>
    </form>
  );
}
